import * as React from 'react';
import { observer } from 'mobx-react';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { AmountColor } from 'typings/enums';
import BudgetBar from 'components/Charts/budgetBar';
import { cycleRules } from 'utils/MobxDataManager/singletons';
import './countryBudgetCard.scss';

export interface ICountryBudgetCardProps {
    countryBudget: ICountryBudget;
    configuration: Configuration;
    overBudgetCount?: number;
}

export class CountryBudgetCard extends React.Component<ICountryBudgetCardProps> {
    constructor(props: ICountryBudgetCardProps) {
        super(props);

        this.handleGoToView = this.handleGoToView.bind(this);
    }

    render(): React.ReactNode {
        const { countryBudget } = this.props;
        const isOverBudget = countryBudget.convertedTotalRemaining < 0;
        const budgetTypeClass = `nomst-budget-card--${this.budgetClass}`;
        const overBudgetClass = isOverBudget ? 'nomst-budget-card--overbudget' : '';

        return (
            <div className={`nomst-budget-card ${budgetTypeClass} ${overBudgetClass}`}>
                <h4 className="nomst-budget-card__name">
                    {countryBudget.name}
                    {this.renderTotalPercentageText()}
                </h4>

                <p className="nomst-budget-card__associated-fields">
                    {this.renderAssociatedFieldsText()}
                </p>
                <p className="nomst-budget-card__total">
                    Budget
                    <span className="nomst-budget-card__total-amount">
                        {this.printBudgetValue(countryBudget.convertedTotalAmount)}
                    </span>
                </p>
                <div className="nomst-budget-card__bar">
                    <BudgetBar
                        className="nomst-budget-card__bar-line"
                        spent={countryBudget.convertedTotalSpent}
                        total={countryBudget.convertedTotalAmount}
                        budgetType={this.budgetClass}
                    />
                </div>
                <div className="nomst-budget-card__status">
                    {this.renderOverBudget()}
                    <div className="nomst-budget-card__remaining" data-mf-replace-inner="***">
                        {this.printBudgetValue(countryBudget.convertedTotalRemaining)}
                    </div>
                </div>
            </div>
        );
    }

    private get budgetClass(): string {
        const { countryBudget, configuration } = this.props;
        const budgetColor = configuration.getCycleBudgetColor(countryBudget.budgetCycleId);
        switch (budgetColor) {
            case AmountColor.Increase: return 'budget-increase';
            case AmountColor.Bonus: return 'budget-bonus';
            case AmountColor.MarketAdjustment: return 'budget-market';
            case AmountColor.LumpSum: return 'budget-lump';
            case AmountColor.Promotion: return 'budget-promotion';
            case AmountColor.VariablePay: return 'budget-variable';
            case AmountColor.Stock: return 'budget-stock';
            default: return '';
        }
    }

    private renderTotalPercentageText(): React.ReactNode {
        if (cycleRules.isCurrentCycleClosed) {
            return null;
        }

        const { countryBudget, configuration } = this.props;
        const totalPercentageText = configuration.format.formatDecimalToPercentage(
            countryBudget.totalPercentage);
        return (
            <span
                className="nomst-budget-card__percent"
                hidden={countryBudget.isStockBudget}
            >
                ({totalPercentageText})
            </span>
        );
    }

    private renderAssociatedFieldsText(): string {
        const { countryBudget, configuration } = this.props;
        const associatedFields = configuration
            .getBudgetCycleFieldsTitle(countryBudget.budgetCycleId);
        return associatedFields.length ? associatedFields.join(', ') : 'None';
    }

    private renderOverBudget(): React.ReactNode {
        const { overBudgetCount } = this.props;
        if (!overBudgetCount) {
            return null;
        }

        return (
            <div className="nomst-budget-card__overbudget">
                <button className="pxl-link" onClick={this.handleGoToView}>
                    {`${overBudgetCount} Team${overBudgetCount > 1 ? 's' : ''} Over Budget`}
                </button>
            </div>
        );
    }

    private printBudgetValue(value: number): string {
        const { countryBudget, configuration } = this.props;
        if (countryBudget.isStockBudget && countryBudget.isStockInUnitOnly) {
            return `${configuration.format.formatStockDecimal(value)} Shares`;
        }

        return configuration.format.formatCurrencyValue(
            countryBudget.currentIsoCurrencyCode,
            value,
            false);
    }

    private handleGoToView(): Promise<void> {
        const { configuration, countryBudget } = this.props;
        return navigateToNoMst.navigateToOverBudgetManagersView(
            configuration.cycleId,
            null,
            countryBudget.budgetCycleId);
    }
}

export default observer(CountryBudgetCard);